import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Users, Search, Mic } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CobaltTriangle, OrangeArc, StripedTriangle } from "../components/Motifs";

const services = [
  {
    icon: Compass,
    title: "Partnership Strategy & System Design",
    intro:
      "Helping organizations, state leaders, and communities design learner-centered ecosystems that authentically connect education, workforce, and economic opportunity.",
    items: [
      "Drafting and review of strategic plans",
      "Stakeholder and power-mapping facilitation",
      "Career pathways and talent pipeline design",
      "Program launch plans and implementation roadmaps",
    ],
    color: "cobalt",
  },
  {
    icon: Users,
    title: "Facilitation & Convening",
    intro:
      "Designing meetings people actually remember — collaborations where relationships deepen, ideas emerge, and decisions move forward.",
    items: [
      "Partnership convenings and leadership retreat design and facilitation",
      "Design and launch of role-alike communities of practice",
      "Facilitation of human-centered workshops",
      "Cross-sector partnership strategy",
    ],
    color: "orange",
  },
  {
    icon: Search,
    title: "Research & Insight",
    intro:
      "Turning labor market information, policy, stakeholder voices, and global examples into practical recommendations.",
    items: [
      "Stakeholder engagement, interviews, and prioritization planning",
      "Landscape scans and labor market analysis",
      "Event design and presentation support",
      "Stakeholder synthesis and policy recommendations",
    ],
    color: "cobalt",
  },
  {
    icon: Mic,
    title: "Advising & Thought Partnership",
    intro:
      "Conference sessions, topic-based workshops, and strategic advising focused on the future of learning and work.",
    items: [
      "Milestone planning",
      "Thought partnership for new and emerging strategies",
      "Mid-career leadership coaching",
      "Topic-specific coaching, research, and subject-matter expertise",
    ],
    color: "orange",
  },
];

const colorMap = {
  cobalt: {
    bg: "bg-cobalt",
    ring: "ring-cobalt/20",
    text: "text-cobalt",
    hover: "hover:bg-cobalt-deep",
    dot: "bg-cobalt",
    label: "text-cobalt",
    accentSoft: "bg-cobalt/8",
  },
  orange: {
    bg: "bg-orange-brand",
    ring: "ring-orange-brand/20",
    text: "text-orange-brand",
    hover: "hover:bg-orange-soft",
    dot: "bg-orange-brand",
    label: "text-orange-brand",
    accentSoft: "bg-orange-brand/8",
  },
};

const Services = () => {
  return (
    <div data-testid="services-page" className="overflow-hidden">
      {/* ---------- HERO ---------- */}
      <section className="relative">
        <StripedTriangle className="absolute -top-6 -right-6 opacity-60 hidden md:block" size={220} flip />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-12 lg:pt-16 pb-8">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <Reveal>
                <div className="overline">Explore our services</div>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 font-display text-2xl sm:text-3xl lg:text-[2rem] leading-[1.3] text-ink max-w-3xl">
                  <em className="italic text-cobalt">Curiosity is a strategy.</em>{" "}
                  Elenem Strategies was created from a simple belief that the people closest to the challenge
                  should be closest to the solution.
                </p>
              </Reveal>
            </div>
          </div>
        </div>

        {/* 4 circles nav / anchor row */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-12 lg:pb-16">
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-10" data-testid="services-anchors">
              {services.map((s, i) => {
                const c = colorMap[s.color];
                const Icon = s.icon;
                return (
                  <a
                    key={s.title}
                    href={`#svc-${i}`}
                    data-testid={`service-anchor-${i}`}
                    className="group flex flex-col items-center text-center"
                  >
                    <div
                      className={`${c.bg} w-28 h-28 lg:w-32 lg:h-32 rounded-full flex items-center justify-center text-cream shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-transform duration-500 group-hover:scale-105 group-hover:-translate-y-1`}
                    >
                      <Icon size={32} strokeWidth={1.5} />
                    </div>
                    <div className={`mt-4 overline ${c.label}`}>0{i + 1}</div>
                    <div className="mt-1 font-display text-base lg:text-lg text-ink leading-snug max-w-[15ch]">
                      {s.title}
                    </div>
                  </a>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- 4 SERVICE SECTIONS ---------- */}
      <section className="pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-24 lg:space-y-32">
          {services.map((s, i) => {
            const c = colorMap[s.color];
            const Icon = s.icon;
            const reverse = i % 2 === 1;
            return (
              <div
                key={s.title}
                id={`svc-${i}`}
                data-testid={`service-section-${i}`}
                className="scroll-mt-28"
              >
                <div
                  className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                    reverse ? "lg:[&>*:first-child]:order-2" : ""
                  }`}
                >
                  {/* Big colored circle */}
                  <Reveal className="lg:col-span-5">
                    <div className="relative flex justify-center lg:justify-start">
                      <div
                        className={`${c.bg} relative w-72 h-72 lg:w-96 lg:h-96 rounded-full flex flex-col items-center justify-center text-cream shadow-[0_30px_60px_rgb(0,0,0,0.12)]`}
                      >
                        <span className="font-display text-8xl lg:text-9xl leading-none opacity-25 absolute">
                          0{i + 1}
                        </span>
                        <div className="relative z-10 flex flex-col items-center">
                          <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full border-2 border-cream/60 flex items-center justify-center backdrop-blur-sm">
                            <Icon size={30} strokeWidth={1.5} />
                          </div>
                        </div>
                      </div>
                      {/* Small companion motif */}
                      {s.color === "cobalt" ? (
                        <OrangeArc className="absolute -bottom-4 -right-2 lg:-right-4" size={70} />
                      ) : (
                        <CobaltTriangle className="absolute -top-3 -right-3 lg:-right-6" size={70} flip />
                      )}
                    </div>
                  </Reveal>

                  {/* Content */}
                  <Reveal delay={0.1} className="lg:col-span-7">
                    <div>
                      <div className={`overline ${c.label}`}>0{i + 1} · What we do</div>
                      <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-ink">
                        {s.title}
                      </h2>
                      <p className="mt-6 text-lg text-ink-soft leading-relaxed max-w-xl">
                        {s.intro}
                      </p>

                      <ul className="mt-8 space-y-3 max-w-xl">
                        {s.items.map((item, j) => (
                          <li
                            key={item}
                            data-testid={`service-item-${i}-${j}`}
                            className="flex items-start gap-4 group"
                          >
                            <span
                              className={`mt-1 w-6 h-6 flex items-center justify-center rounded-full ${c.bg} text-cream text-xs font-medium flex-shrink-0 transition-transform group-hover:scale-110`}
                            >
                              {j + 1}
                            </span>
                            <span className="text-ink leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Reveal>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------- CLOSING CTA ---------- */}
      <section className="relative py-20 lg:py-28 bg-cream-alt overflow-hidden">
        <OrangeArc className="absolute -top-10 -right-10" size={200} />
        <StripedTriangle className="absolute -bottom-10 -left-10 opacity-70" size={220} />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div>
              <div className="overline">Let's explore</div>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-ink">
                Not sure which fit is right?
              </h2>
              <p className="mt-4 text-ink-soft text-lg leading-relaxed">
                Every engagement begins with a conversation. Tell me what you're building — we'll figure out the
                shape of the work together.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
              <Link
                to="/collaborate"
                data-testid="services-collaborate-cta"
                className="inline-flex items-center gap-2 bg-cobalt text-cream px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-cobalt-deep transition-colors"
              >
                Collaborate <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/work"
                data-testid="services-work-cta"
                className="inline-flex items-center gap-2 border border-ink text-ink px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-ink hover:text-cream transition-colors"
              >
                See past work
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Services;
